import { Dispatch, MouseEvent, SetStateAction } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  ButtonBase,
  styled,
  Typography,
  useMediaQuery,
} from "@mui/material";
import UserAvatar from "../../Common/UserAvatar";
import {
  HighlightOffOutlined,
  PersonOutline,
  SettingsOutlined,
  SupervisorAccountOutlined,
} from "@mui/icons-material";
import Menu from "./Menu";
import IconMenuItem from "./IconMenuItem";
import { useAppContext } from "../../../libs/contextLib";
import FT from "../../../libs/FT";
import { useMenuState } from "./menu-state-hook";
import { userProfile } from "../../../libs/UserProfile";
import { logEvent } from "../../../libs/amplitude";
import { collapseSvg } from "../../UserHome/Base/SVG";
import { QuotaInfo, UserInfo } from "../../UserHome/Base/Sidebar/AccountInfo";

interface Props {
  navigateToProfileEdit: (
    event: MouseEvent<Element>,
    path: string
  ) => Promise<void>;
  userInfo: UserInfo;
  userQuota: any;
}

export default function UserAvatarMenu({
  navigateToProfileEdit,
  userInfo,
  userQuota,
}: Props) {
  const menuState = useMenuState();
  const history = useHistory();
  const userCache = userProfile(localStorage);
  const isTab = useMediaQuery("(min-width:769px) and (max-width:1023px)");

  const { userHasAuthenticated, user, setUser } =
    useAppContext() as unknown as {
      userHasAuthenticated: Dispatch<SetStateAction<boolean>>;
      user: any;
      setUser: (user: any) => void;
    };

  const userSignOut = (event: MouseEvent<Element>) => {
    menuState.onClose(event);
    userCache.clear();
    userHasAuthenticated(false);
    history.push("/login");
  };

  const adminSignOut = async (
    event: MouseEvent<Element>,
    adminResponse: any
  ) => {
    menuState.onClose(event);
    userCache.setToken(adminResponse.jwt);
    userCache.setProvider(adminResponse.provider);
    userCache.setProviderId(adminResponse.providerId);
    userCache.setUserId(adminResponse.userId);
    setUser(await FT.getAuthorizedUser());
    history.push("/admin");
  };

  function signOut(event: MouseEvent<Element>) {
    if (userCache.provider() !== "dummy") {
      userSignOut(event);
    } else {
      (async () => {
        try {
          const adminResponse = await FT.unimpersonate();
          adminSignOut(event, adminResponse);
        } catch {
          userSignOut(event);
        }
      })();
    }
  }
  function adminLink(event: MouseEvent<Element>) {
    menuState.onClose(event);
    history.push("/admin");
  }
  function profileLink(event: MouseEvent<Element>) {
    menuState.onClose(event);
    logEvent("ProfileMenu", userCache.userId());
    history.push(`/profile/${userCache.userId()}`);
  }
  return (
    <>
      <StyledButtonBase
        ref={menuState.anchorRef}
        onClick={menuState.toggle}
        disableRipple
        sx={{
          "& .MuiAvatar-img": {
            border: menuState.open ? "1px solid #FDD649" : "none",
          },
        }}
      >
        <Box>
          <UserAvatar />
        </Box>

        {!isTab && (
          <Box
            sx={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                textAlign: "left",
              }}
            >
              <Typography
                sx={{
                  color: "#fefefe",
                  textOverflow: "ellipsis",
                  WebkitLineClamp: 1,
                  width: "130px",
                  overflow: "hidden",
                  fontSize: "14px",
                }}
              >
                {userInfo && userInfo.name}
              </Typography>
              <Typography
                sx={{
                  color: "#b7b7b7",
                  textOverflow: "ellipsis",
                  WebkitLineClamp: 1,
                  width: "130px",
                  overflow: "hidden",
                  fontSize: "12px",
                }}
              >
                {userInfo && userInfo.emailAddress}
              </Typography>
            </Box>
            <Box>{collapseSvg}</Box>
          </Box>
        )}
      </StyledButtonBase>

      <Box style={{ zIndex: 999, position: "fixed", top: "0px" }}>
        {/* temp */}
        <Menu {...menuState}>
          {isTab && (
            <Box sx={{ padding: "0 16px" }}>
              <StyledButtonBase
                // ref={menuState.anchorRef}
                onClick={menuState.toggle}
                disableRipple
                sx={{
                  mb: "8px",
                  "& .MuiAvatar-img": {
                    border: menuState.open ? "1px solid #FDD649" : "none",
                  },
                }}
              >
                <Box>
                  <UserAvatar />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      textAlign: "left",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#fefefe",
                        textOverflow: "ellipsis",
                        WebkitLineClamp: 1,
                        width: "130px",
                        overflow: "hidden",
                        fontSize: "14px",
                      }}
                    >
                      {userInfo && userInfo.name}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#b7b7b7",
                        textOverflow: "ellipsis",
                        WebkitLineClamp: 1,
                        width: "130px",
                        overflow: "hidden",
                        fontSize: "12px",
                      }}
                    >
                      {userInfo && userInfo.emailAddress}
                    </Typography>
                  </Box>
                </Box>
              </StyledButtonBase>
              <QuotaInfo
                userInfo={userInfo}
                userQuota={userQuota}
                navigateToProfileEdit={navigateToProfileEdit}
              />
              <Box
                sx={{
                  height: "1px",
                  backgroundColor: "#292929",
                  width: "100%",
                  mt: "8px",
                }}
              ></Box>
            </Box>
          )}
          {user?.isAdmin && (
            <IconMenuItem
              onClick={adminLink}
              icon={<SupervisorAccountOutlined />}
              primary={<StyledMenuText>Admin</StyledMenuText>}
            />
          )}

          <IconMenuItem
            onClick={profileLink}
            icon={<PersonOutline />}
            primary={<StyledMenuText>Profile</StyledMenuText>}
          />

          <IconMenuItem
            onClick={(e) =>
              navigateToProfileEdit(e as unknown as MouseEvent<Element>, "")
            }
            icon={<SettingsOutlined />}
            primary={<StyledMenuText>Settings</StyledMenuText>}
          />

          <IconMenuItem
            onClick={signOut}
            icon={<HighlightOffOutlined color="error" />}
            primary={<StyledMenuText color="error">Sign Out</StyledMenuText>}
          />
        </Menu>
      </Box>
    </>
  );
}

const StyledButtonBase = styled(ButtonBase)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  alignItems: "center",
  gap: "8px",
  "& .MuiAvatar-img": {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    padding: 0,
  },
  "& .MuiAvatar-root": {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    padding: 0,
  },
  [theme.breakpoints.down("xs")]: {
    paddingTop: "3px",
    paddingRight: "10px",
  },
  [theme.breakpoints.down(1024)]: {
    width: "unset",
  },
}));

const StyledMenuText = styled(Typography)({
  color: "#fefefe",
});
